// src/components/OnboardingPanel.js
// Vendor go-live flow: business profile (KYB) + checklist + terms + submit.
// Shown in the vendor dashboard while Vendor.status != ACTIVE.

import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const EMPTY = { legalName:'', tradeLicenseNo:'', tradeLicenseExpiry:'', trn:'', contactName:'', contactPhone:'' };

const toForm = (p = {}) => ({
  legalName:          p.legalName || '',
  tradeLicenseNo:     p.tradeLicenseNo || '',
  tradeLicenseExpiry: p.tradeLicenseExpiry ? String(p.tradeLicenseExpiry).slice(0, 10) : '',
  trn:                p.trn || '',
  contactName:        p.contactName || '',
  contactPhone:       p.contactPhone || '',
});

// Where a vendor goes to fix each item that is theirs to fix
const HINTS = {
  tapApproved:       'Completed by your mall administrator with the payment provider',
  pricingAssigned:   'Assigned by the platform administrator',
  logoUploaded:      'Upload your logo in the Admin tab',
  menuReady:         'Add at least one active item in the Admin tab',
  testOrderComplete: 'Place a test order and mark it completed',
};

export default function OnboardingPanel() {
  const { updateUser } = useAuth();
  const [data,    setData]    = useState(null);
  const [form,    setForm]    = useState(EMPTY);
  const [msg,     setMsg]     = useState('');
  const [busy,    setBusy]    = useState(false);
  const [contract, setContract] = useState(null); // { version, title, text }
  const [agreed,   setAgreed]   = useState(false);
  const [loadErr, setLoadErr] = useState('');

  const apply = useCallback((d) => {
  setData(d);
  if (d?.status) updateUser({ status: d.status });
 }, []);

 const load = useCallback(async () => {
  try {
    const r = await api.get('/vendors/me/onboarding');
    apply(r.data); setForm(toForm(r.data.profile));
  } catch (err) { setLoadErr(err.response?.data?.error || 'Could not load onboarding'); }
 }, []);

 useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get('/vendors/me/onboarding/contract').then(r => setContract(r.data)).catch(() => setContract(null));
  }, []);

  const flash = (text) => { setMsg(text); setTimeout(() => setMsg(''), 4000); };
  const errText = (err, fallback) => {
    const e = err.response?.data;
    return '❌ ' + (e?.missing?.length ? `${e.error}: ${e.missing.join(', ')}` : e?.error || fallback);
  };

  const saveProfile = async () => {
    setBusy(true);
    try {
      const r = await api.patch('/vendors/me/onboarding', form);
      apply(r.data); setForm(toForm(r.data.profile)); flash('✅ Profile saved');
    } catch (err) { flash(errText(err, 'Save failed')); }
    finally { setBusy(false); }
  };

  const acceptTerms = async () => {
    setBusy(true);
    try {
      await api.post('/vendors/me/onboarding/accept-contract', { version: data.contract.currentVersion });
      await load(); flash('✅ Terms accepted');
    } catch (err) { flash(errText(err, 'Could not record acceptance')); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/vendors/me/onboarding/submit');
      await load(); flash('✅ Submitted for review');
    } catch (err) { flash(errText(err, 'Submit failed')); }
    finally { setBusy(false); }
  };

  if (loadErr) return <div className="page"><div className="card" style={{ color:'var(--red)' }}>{loadErr}</div></div>;
  if (!data)   return <div className="page"><div className="card">Loading...</div></div>;

  const termsDone = !!data.contract.acceptedAt;
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const field = (k, label, props = {}) => (
    <div>
      <div className="section-label" style={{ marginBottom:6 }}>{label}</div>
      <input value={form[k]} onChange={set(k)} {...props} />
    </div>
  );

  return (
    <div className="page">
      <div className="page-title" style={{ marginBottom:4 }}>🚀 Go Live</div>
      <div className="page-sub" style={{ marginBottom:20 }}>
        {data.completed} of {data.total} steps complete
      </div>

      {msg && <div className="card" style={{ marginBottom:14, padding:'10px 14px' }}>{msg}</div>}

      {data.status === 'UNDER_REVIEW' && (
        <div className="card" style={{ marginBottom:14 }}>🔵 Submitted — your mall administrator is reviewing your account.</div>
      )}
      {data.status === 'SUSPENDED' && (
        <div className="card" style={{ marginBottom:14, color:'var(--red)' }}>🔴 Your account is suspended. Contact your mall administrator.</div>
      )}

      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontWeight:700, marginBottom:14 }}>Business Profile</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:14, marginBottom:16 }}>
          {field('legalName', 'Legal name')}
          {field('tradeLicenseNo', 'Trade licence no.')}
          {field('tradeLicenseExpiry', 'Trade licence expiry', { type:'date', style:{ width:'100%' } })}
          {field('trn', 'TRN (15 digits, optional)', { inputMode:'numeric', maxLength:15 })}
          {field('contactName', 'Contact name')}
          {field('contactPhone', 'Contact phone')}
        </div>
        <button className="btn btn-primary" onClick={saveProfile} disabled={busy}>Save Profile</button>
      </div>

      <div className="card">
        <div style={{ fontWeight:700, marginBottom:14 }}>Go-live Checklist</div>
        {Object.entries(data.checklist).map(([key, it]) => (
          <div key={key} style={{ display:'flex', gap:10, padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
            <span>{it.done ? '✅' : '⏳'}</span>
            <div>
              <div style={{ fontWeight:600 }}>
                {it.label}{key === 'tapApproved' && it.cashOnly ? ' (not required — cash-only mall)' : ''}
              </div>
              {!it.done && HINTS[key] && <div style={{ fontSize:12, color:'var(--text3)' }}>{HINTS[key]}</div>}
            </div>
          </div>
        ))}

        {!termsDone && (
          <div style={{ marginTop:16 }}>
            <div style={{ fontWeight:600, marginBottom:6 }}>{contract?.title || 'Terms & Conditions'}</div>
            <div style={{ maxHeight:240, overflowY:'auto', whiteSpace:'pre-wrap', fontSize:12, lineHeight:1.55, padding:12, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8 }}>
              {contract ? contract.text : 'Loading terms...'}
            </div>
            <label style={{ display:'flex', gap:8, alignItems:'center', marginTop:10, fontSize:13 }}>
              <input type="checkbox" checked={agreed} disabled={!contract} onChange={e => setAgreed(e.target.checked)} style={{ width:'auto' }} />
              I have read and agree to these terms (v{contract?.version || data.contract.currentVersion})
            </label>
          </div>
        )}

        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginTop:16, alignItems:'center' }}>
          <button className="btn btn-primary" onClick={acceptTerms} disabled={busy || termsDone || !agreed}>
            {termsDone ? `✅ Terms v${data.contract.acceptedVersion} accepted` : `Accept Terms & Conditions (v${data.contract.currentVersion})`}
          </button>
          {data.status === 'PENDING_KYC' && (
            <button className="btn btn-primary" onClick={submit} disabled={busy}>Submit for Review</button>
          )}
        </div>
        {data.status === 'PENDING_KYC' && (
          <div style={{ fontSize:12, color:'var(--text3)', marginTop:8 }}>
            Submitting needs your profile, a valid licence and accepted terms. Your mall administrator completes the rest before approving.
          </div>
        )}
      </div>
    </div>
  );
}
